import { i18nNsFactory } from '@atlas-frontend/i18n';
import { TextPlaceholder } from '../../common/components/typography';
import translations from '../translations/DeadlinesTable.translations.json';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import {
  DataObjectValidFormDataType,
  DeadlineGenerationFormType,
  ExecutionDeadlineFormDataType,
} from '../schema';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import {
  CloneFormTableRowButton,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  FormField,
  getFormTableFieldName,
  TanstackFormTable,
} from '../../common/form';
import { Button, Flex } from 'antd';
import DatepickerLocale from 'antd/es/date-picker/locale/et_EE';
import { Tooltip } from '../../common/components/Tooltip';
import { formatDate, getUtcDayjs, ISODate } from '../../common/utils';
import styled from 'styled-components';
import { FormDatePicker } from '../../common/form/input/FormDatePicker';
import { FormTimePicker } from '../../common/form/input/FormTimePicker';
import { useFormContext, useFormState } from 'react-hook-form';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { getRowHtmlAttributes } from './utils';
import { DeadlinesPeriodCheckbox } from './DeadlinesPeriodCheckbox';
import { DeadlinesGenerateModal } from './DeadlinesGenerateModal';
import { DeadlinesCheckModal } from './DeadlinesCheckModal';
import { useValidateDeadlines } from './query/useValidateDeadlines';
import { ApiError } from '../../common/ApiClient';
import { useGenerateDeadlines } from './query/useGenerateDeadlines';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'deadlines-edit-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<ExecutionDeadlineFormDataType>();

export const defaultDeadlineData =
  (): Partial<ExecutionDeadlineFormDataType> => ({
    id: null,
    executionId: null,
    dueTime: '23:59',
  });

export interface DeadlinesEditTableProps {
  onDeadlineValidate: () => void;
  onDeadlineValidateErrors: (errors: ApiError[]) => void;
  onDeadlineValidateSuccess: () => void;
}

export const DeadlinesEditTable: FC<DeadlinesEditTableProps> = ({
  onDeadlineValidate,
  onDeadlineValidateErrors,
  onDeadlineValidateSuccess,
}) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const [isPastYear, setIsPastYear] = useState(true);
  const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
  const [isCheckModalVisible, setIsCheckModalVisible] = useState(false);
  const [generateError, setGenerateError] = useState<string | undefined>();
  const [checkError, setCheckError] = useState<string | undefined>();
  const { isDirty } = useFormState();
  const { mutateAsync: validateAsync, isPending: isValidating } =
    useValidateDeadlines();
  const { mutateAsync: generateAsync, isPending: isGenerating } =
    useGenerateDeadlines();

  const onGenerate = useCallback(
    (formData: DeadlineGenerationFormType) => {
      setGenerateError(undefined);

      return generateAsync({
        dataObjectVersionId: getValues('versionId'),
        formData,
      })
        .then((deadlines) => {
          const currentDeadlines: ExecutionDeadlineFormDataType[] =
            getValues('executionDeadlines');

          const error = validateGenerated(currentDeadlines, deadlines);
          if (error) {
            setGenerateError(error);
            return;
          }

          setValue('executionDeadlines', [...currentDeadlines, ...deadlines], {
            shouldDirty: true,
          });
          setIsGenerateModalVisible(false);
        })
        .catch((error) => {
          setGenerateError(error.response?.data.message ?? error.message);
          throw error;
        });
    },
    [generateAsync, getValues, setValue],
  );

  const onGenerateCancel = useCallback(() => {
    setGenerateError(undefined);
    setIsGenerateModalVisible(false);
  }, []);

  const onCheck = useCallback(
    (dateRange: [ISODate, ISODate]) => {
      setCheckError(undefined);
      onDeadlineValidate();

      const [from, to] = [getUtcDayjs(dateRange[0]), getUtcDayjs(dateRange[1])];
      const currentDeadlines: ExecutionDeadlineFormDataType[] =
        getValues('executionDeadlines');
      const valueDatesWithinRange = currentDeadlines
        .map((deadline) => deadline.valueDate)
        .filter((valueDate) =>
          getUtcDayjs(valueDate).isBetween(from, to, 'day', '[]'),
        );

      if (valueDatesWithinRange.length === 0) {
        setCheckError(t('check.error.no-deadlines'));
        return;
      }

      return validateAsync({
        dataObjectVersionId: getValues('versionId'),
        period: dateRange,
      })
        .then(() => {
          onDeadlineValidateSuccess();
          setIsCheckModalVisible(false);
        })
        .catch((error) => {
          onDeadlineValidateErrors(error.response?.data.errors ?? []);
          setIsCheckModalVisible(false);
          throw error;
        });
    },
    [
      getValues,
      onDeadlineValidate,
      onDeadlineValidateErrors,
      onDeadlineValidateSuccess,
      t,
      validateAsync,
    ],
  );

  const onCheckCancel = useCallback(() => {
    setCheckError(undefined);
    setIsCheckModalVisible(false);
  }, []);

  const onChange = useCallback(
    (e: CheckboxChangeEvent) => {
      setIsPastYear(e.target.checked);
    },
    [setIsPastYear],
  );

  const rowHtmlAttributes = useCallback(
    (row: ExecutionDeadlineFormDataType, rowIndex: number) => {
      const valueDate = getValues(`executionDeadlines.${rowIndex}.valueDate`);
      return getRowHtmlAttributes(valueDate, isPastYear);
    },
    [isPastYear, getValues],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => formatDate(rowData.valueDate), {
        id: 'valueDate',
        header: t('column.valueDate'),
        meta: { width: 120 },
        cell: ValueDateCell,
        enableColumnFilter: false,
        sortingFn: 'formattedDate',
      }),
      columnHelper.accessor(
        (rowData) =>
          rowData.dueDate
            ? `${formatDate(rowData.dueDate)} ${rowData.dueTime}`
            : rowData.dueTime,
        {
          id: 'dueDate',
          header: t('column.dueDate'),
          cell: (props) => <DueDateCell {...props} />,
          enableColumnFilter: false,
          meta: {
            width: 250,
          },
          sortingFn: 'formattedDateTime',
        },
      ),
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton
              title={t('button.add-deadline')}
              autoFocusField="valueDate"
              defaultRowValues={defaultDeadlineData}
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <Flex justify="center" gap="small">
            <CloneFormTableRowButton context={props} resetId />
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        meta: {
          className: 'sticky-right',
          width: 85,
          minWidth: 85,
        },
      }),
    ],
    [t],
  );

  return (
    <>
      <TableWrapper vertical gap="middle">
        <TanstackFormTable
          name="executionDeadlines"
          actions={
            <>
              <DeadlinesPeriodCheckbox
                onChange={onChange}
                showPastYear={isPastYear}
              />
              <Button
                id="generate-deadlines"
                onClick={() => setIsGenerateModalVisible(true)}
              >
                {t('button.generate-deadlines')}
              </Button>
              {isDirty ? (
                <Tooltip
                  title={t('button.check-deadlines.unsaved-tooltip')}
                  placement="top-start"
                >
                  <Button id="check-deadlines" disabled>
                    {t('button.check-deadlines.title')}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  id="check-deadlines"
                  onClick={() => setIsCheckModalVisible(true)}
                >
                  {t('button.check-deadlines.title')}
                </Button>
              )}
            </>
          }
          actionsInFront
          columns={columns}
          defaultSort={[{ id: 'valueDate', desc: true }]}
          rowHtmlAttributes={rowHtmlAttributes}
          sticky
          virtual
          scrollContainerSelector="#detail-layout-main-content"
        />
      </TableWrapper>
      {isGenerateModalVisible && (
        <DeadlinesGenerateModal
          onGenerate={onGenerate}
          onCancel={onGenerateCancel}
          generateError={generateError}
          isGenerating={isGenerating}
        />
      )}
      {isCheckModalVisible && (
        <DeadlinesCheckModal
          onCheck={onCheck}
          onCancel={onCheckCancel}
          checkError={checkError}
          isValidating={isValidating}
        />
      )}
    </>
  );
};

const ValueDateCell: FC<CellContext<ExecutionDeadlineFormDataType, any>> = memo(
  (props) => {
    const name = getFormTableFieldName(props);
    return (
      <FormField
        name={name}
        size="small"
        preview={{
          render: (value) => formatDate(value),
        }}
      >
        <FormDatePicker name={name} autoFocus size="small" defaultOpen />
      </FormField>
    );
  },
);

const DueDateCell: FC<CellContext<ExecutionDeadlineFormDataType, any>> = memo(
  (props) => {
    const name = getFormTableFieldName(props);
    const timeName = `executionDeadlines.${props.row.index}.dueTime` as const;
    const { getValues } = useFormContext<DataObjectValidFormDataType>();
    return (
      <FormField
        name={name}
        size="small"
        preview={{
          render: (value) => (
            <>
              {value ? (
                formatDate(value)
              ) : (
                <TextPlaceholder>
                  {DatepickerLocale.lang.placeholder}
                </TextPlaceholder>
              )}
              {`, ${getValues(timeName)}`}
            </>
          ),
        }}
      >
        <Flex gap="middle">
          <FormDatePicker name={name} size="small" autoFocus defaultOpen />
          <FormTimePicker
            name={timeName}
            size="small"
            timeFormat="HH:mm"
            allowClear={false}
          />
        </Flex>
      </FormField>
    );
  },
);

const validateGenerated = (
  original: ExecutionDeadlineFormDataType[],
  generated: ExecutionDeadlineFormDataType[],
) => {
  if (!generated.length) {
    return t('generate.error.no-deadlines');
  }

  const anyExistingDeadlineWithMatchingValueDate = generated.some(
    (generatedDeadline) =>
      original.some(
        (originalDeadline) =>
          originalDeadline.valueDate === generatedDeadline.valueDate,
      ),
  );
  if (anyExistingDeadlineWithMatchingValueDate) {
    return t('generate.error.duplicate-deadlines');
  }
};

const TableWrapper = styled(Flex)`
  width: 100%;
`;
